import { NextSeo } from "next-seo";
import { ReactElement, ReactNode } from "react";
import { RecentPostsSection } from "~/components/blog/RecentPostsSection";
import InspectionFrequencySelector from "~/components/InspectionFrequencySelector";
import { MainLayout } from "~/components/layouts/MainLayout";
import {
  SolutionsLandingPage,
  type SolutionsSection,
} from "~/components/SolutionsLandingPage";
import type { PostMeta } from "~/lib/mdx";
import { NextPageWithLayout } from "~/pages/_app";
import electricalServiceImage from "~/src/assets/images/hero-power-box.webp";
import gasServiceImage from "~/src/assets/images/gas_10.jpg";
import smokeServiceImage from "~/src/assets/images/smoke-alarm-check.webp";

type SolutionsPageContent = {
  section1: {
    title: string;
    paragraphs: string[];
  };
  section2: {
    title: string;
    paragraphs: string[];
  };
  section3?: {
    title: string;
    paragraphs: string[];
  };
  seo: {
    title: string;
    description: string;
  };
};

type SolutionsPageFactoryOptions = {
  content: SolutionsPageContent;
  faqSelectedIds: number[];
  bannerContent?: ReactNode;
  sections?: SolutionsSection[];
};

export const buildDefaultSolutionsSections = (
  content: SolutionsPageContent,
  additionalContent?: ReactNode
): SolutionsSection[] => {
  const sections: SolutionsSection[] = [
    {
      title: content.section1.title,
      paragraphs: content.section1.paragraphs,
      image: smokeServiceImage,
      imageAlt: "Technik kontrolujący system bezpieczeństwa w budynku",
    },
    {
      title: content.section2.title,
      paragraphs: content.section2.paragraphs,
      image: gasServiceImage,
      imageAlt: "Specjalista kontrolujący instalację gazową w kuchni",
      reverse: true,
      additionalContent,
    },
  ];

  if (content.section3) {
    sections.push({
      title: content.section3.title,
      paragraphs: content.section3.paragraphs,
      image: electricalServiceImage,
      imageAlt: "Technik wykonujący pomiary rozdzielnicy elektrycznej",
    });
  }

  return sections;
};

type SolutionsPageProps = { recentPosts?: PostMeta[] };

export const createSolutionsPage = ({
  content,
  faqSelectedIds,
  bannerContent,
  sections,
}: SolutionsPageFactoryOptions): NextPageWithLayout<SolutionsPageProps> => {
  const resolvedSections = sections ?? buildDefaultSolutionsSections(content);
  const resolvedBannerContent = bannerContent ?? <InspectionFrequencySelector />;

  const SolutionsPage: NextPageWithLayout<SolutionsPageProps> = ({ recentPosts }) => {
    return (
      <>
        <SolutionsLandingPage
          bannerTitle={content.seo.title}
          bannerContent={resolvedBannerContent}
          sections={resolvedSections}
          faqSelectedIds={faqSelectedIds}
        />
        {recentPosts && <RecentPostsSection posts={recentPosts} />}
      </>
    );
  };

  SolutionsPage.getLayout = function GetLayout(page: ReactElement) {
    return (
      <MainLayout>
        <NextSeo
          title={content.seo.title}
          description={content.seo.description}
        />
        {page}
      </MainLayout>
    );
  };

  return SolutionsPage;
};
